(() => {
  'use strict';
  const normalize = (value, locale) => String(value || '').toLocaleLowerCase(locale).normalize('NFD').replace(/\p{M}/gu, '');
  function displayQuery(value, country, mode = 'collection') {
    if (mode === 'store') return String(value || '').trim();
    const text = String(value || '').trim();
    if (!/^[A-Z]{2}$/.test(country || '')) return text;
    if (text === `tag:${country}`) return '';
    const suffix = ` AND tag:${country}`;
    if (!text.endsWith(suffix)) return text;
    const query = text.slice(0, -suffix.length).trim();
    return query.startsWith('(') && query.endsWith(')') ? query.slice(1, -1) : query;
  }
  function isCatalogPage(url, origin, catalogPath, searchPath) {
    return url.origin === origin && [catalogPath, searchPath].includes(url.pathname.replace(/\/$/, ''));
  }
  function sameCatalogScope(current, candidate) {
    const mode = current.catalogMode;
    if (!['store', 'collection', 'tag_search'].includes(mode) || current.marketAllowed !== 'true' || !current.storeKey) return false;
    if (candidate.marketAllowed !== 'true' || candidate.catalogMode !== mode || candidate.storeKey !== current.storeKey || candidate.locale !== current.locale) return false;
    return mode === 'store' || (/^[A-Z]{2}$/.test(current.countryTag || '') && candidate.countryTag === current.countryTag);
  }
  function filterProducts(products, filters) {
    const query = normalize(filters.query, filters.locale).trim();
    const selected = products.filter(product => (!query || normalize(product.search, filters.locale).includes(query)) &&
      (filters.category === 'all' || product.category === filters.category) &&
      (!filters.promo || product.promo) && (!filters.available || product.available));
    if (filters.sort === 'price-low') selected.sort((a, b) => a.price - b.price);
    else if (filters.sort === 'price-high') selected.sort((a, b) => b.price - a.price);
    else if (filters.sort === 'name') selected.sort((a, b) => a.title.localeCompare(b.title, filters.locale));
    else selected.sort((a, b) => a.order - b.order);
    return selected;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { normalize, displayQuery, filterProducts, isCatalogPage, sameCatalogScope };
  if (typeof document === 'undefined') return;

  function mountCatalog(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = 'true';
    const form = root.querySelector('[data-hlx-catalog-filters]');
    const grid = root.querySelector('[data-hlx-catalog-grid]');
    if (!form || !grid) return;
    const params = new URLSearchParams(window.location.search);
    const locale = root.dataset.locale || document.documentElement.lang || undefined;
    const country = root.dataset.countryTag;
    const catalogMode = root.dataset.catalogMode;
    if (!sameCatalogScope(root.dataset, root.dataset)) return;
    const catalogPath = new URL(root.dataset.catalogUrl, window.location.href).pathname.replace(/\/$/, '');
    const searchPath = new URL(root.dataset.searchUrl, window.location.href).pathname.replace(/\/$/, '');
    // Enhanced filtering spans the full country catalog, beginning with page one.
    if (Number(params.get('page')) > 1) {
      const first = new URL(window.location.href); first.searchParams.delete('page');
      window.location.replace(first.href); return;
    }
    const searchMode = root.dataset.searchMode === 'true';
    if (!searchMode && window.location.pathname.replace(/\/$/, '') !== catalogPath) {
      const scoped = new URL(root.dataset.catalogUrl, window.location.href);
      scoped.search = window.location.search;
      window.location.replace(scoped.href); return;
    }
    const categories = [...root.querySelectorAll('[data-hlx-category]')];
    const more = root.querySelector('[data-hlx-catalog-more]');
    const status = root.querySelector('[data-hlx-selection-count]');
    const fallbackNext = root.querySelector('[data-hlx-next-page]');
    const serverPagination = root.querySelector('[data-hlx-server-pagination]');
    const known = new Set();
    const products = [];
    let limit = 24;
    let loading = false;
    let loadError = false;
    function collect(container) {
      container.querySelectorAll('[data-hlx-catalog-card]').forEach(node => {
        const data = node.dataset;
        if (known.has(data.handle)) return;
        known.add(data.handle);
        products.push({ node, handle: data.handle, category: data.category, title: data.title,
          search: data.search, price: Number(data.price), promo: data.promo === 'true',
          available: data.available === 'true', order: products.length });
      });
    }
    collect(grid);
    const initialCategory = params.get('category') || 'all';
    form.elements.category.value = categories.some(item => item.dataset.hlxCategory === initialCategory) ? initialCategory : 'all';
    form.elements.q.value = displayQuery(params.get('q'), country, catalogMode);
    form.elements.promo.checked = params.get('promo') === '1';
    form.elements.available.checked = params.get('available') === '1';
    form.elements.sort.value = ['featured','price-low','price-high','name'].includes(params.get('sort')) ? params.get('sort') : 'featured';
    const interpolate = (template, value) => template.replace('__COUNT__', String(value));
    function render() {
      const filters = { locale, query: searchMode ? '' : form.elements.q.value, category: form.elements.category.value,
        promo: form.elements.promo.checked, available: form.elements.available.checked, sort: form.elements.sort.value };
      const selected = filterProducts(products, filters);
      const heading = root.querySelector('[data-hlx-catalog-heading]');
      const categoryLink = categories.find(item => item.dataset.hlxCategory === filters.category);
      heading.textContent = form.elements.q.value ? root.dataset.searchHeading.replace('__QUERY__', form.elements.q.value)
        : filters.category === 'all' ? root.dataset.defaultHeading : categoryLink.textContent;
      categories.forEach(item => {
        const active = item.dataset.hlxCategory === filters.category;
        item.classList.toggle('selected', active);
        if (active) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current');
      });
      products.forEach(product => { product.node.hidden = true; });
      selected.forEach((product, index) => { product.node.hidden = index >= limit; grid.append(product.node); });
      root.querySelector('[data-hlx-catalog-count]').textContent = interpolate(root.dataset.countText, selected.length);
      status.textContent = loading ? root.dataset.loadingText : loadError ? root.dataset.errorText : interpolate(root.dataset.selectionText, selected.length);
      root.querySelector('[data-hlx-catalog-empty]').hidden = selected.length > 0 || loading || loadError;
      more.hidden = selected.length <= limit;
    }
    function updateURL() {
      const url = new URL(window.location.href);
      ['category','promo','available','sort'].forEach(key => url.searchParams.delete(key));
      url.searchParams.set('category', form.elements.category.value);
      if (form.elements.promo.checked) url.searchParams.set('promo', '1');
      if (form.elements.available.checked) url.searchParams.set('available', '1');
      if (form.elements.sort.value !== 'featured') url.searchParams.set('sort', form.elements.sort.value);
      history.replaceState(null, '', url);
    }
    form.addEventListener('submit', event => { event.preventDefault(); limit = 24; updateURL(); render(); });
    categories.forEach(item => item.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); form.elements.category.value = item.dataset.hlxCategory;
      limit = 24; updateURL(); render();
    }));
    more.addEventListener('click', () => { limit += 24; render(); });
    render();
    async function loadRemaining() {
      if (!fallbackNext) return;
      let next = fallbackNext.href;
      const visited = new Set();
      loading = true;
      render();
      try {
        while (next && !visited.has(next)) {
          const url = new URL(next, window.location.href);
          if (!isCatalogPage(url, window.location.origin, catalogPath, searchPath)) throw new Error('Invalid catalog page');
          visited.add(next);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok || (response.url && new URL(response.url).origin !== window.location.origin)) throw new Error('Catalog request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = html.querySelector('[data-hlx-page-catalog]');
          if (!page || !sameCatalogScope(root.dataset, page.dataset)) throw new Error('Missing market catalog page');
          collect(page);
          next = page.querySelector('[data-hlx-next-page]')?.getAttribute('href') || '';
          if (next) next = new URL(next, url).href;
        }
        fallbackNext.hidden = true;
        if (serverPagination) serverPagination.hidden = true;
      } catch {
        loadError = true;
      } finally {
        loading = false;
        render();
      }
    }
    loadRemaining();
  }
  function mountCart(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = 'true';
    const form = root.querySelector('[data-hlx-page-cart-form]');
    if (!form) return;
    let busy = false;
    const update = () => {
      if (busy || !form.reportValidity()) return;
      busy = true;
      form.setAttribute('aria-busy', 'true');
      form.requestSubmit(root.querySelector('[data-hlx-page-cart-update]'));
    };
    root.querySelectorAll('[data-hlx-page-quantity]').forEach(button => button.addEventListener('click', () => {
      if (busy) return;
      const input = button.closest('.hlx-page-quantity').querySelector('input');
      input.value = Math.max(1, Math.floor(Number(input.value) || 1) + Number(button.dataset.hlxPageQuantity));
      update();
    }));
    root.querySelectorAll('[data-hlx-page-quantity-input]').forEach(input => input.addEventListener('change', update));
  }
  function mount(scope = document) {
    scope.querySelectorAll('[data-hlx-page-catalog]').forEach(mountCatalog);
    scope.querySelectorAll('[data-hlx-page-cart]').forEach(mountCart);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount()); else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
